import * as React from "react";
import type { SAComp } from "@/data/sellerAnalysisTypes";
import { formatPriceFull, formatBaths } from "@/lib/format";
import { Reveal, Overline } from "@/components/presentation/primitives";
import { DataTag, DataLegend } from "./DataTag";

interface Props {
  comps: SAComp[];
  intro?: string;
}

interface Fact {
  label: string;
  value: React.ReactNode;
}

const CompCard: React.FC<{ comp: SAComp }> = ({ comp }) => {
  const facts: Fact[] = [];
  const sale = formatPriceFull(comp.salePrice);
  const list = formatPriceFull(comp.listPrice);
  const initial = formatPriceFull(comp.initialAsk);
  if (sale) facts.push({ label: "Sold", value: sale });
  if (initial) facts.push({ label: "Initial ask", value: initial });
  if (list) facts.push({ label: "Last asking", value: list });
  if (comp.discountLabel) facts.push({ label: "Discount", value: comp.discountLabel });
  if (comp.saleDate) facts.push({ label: "Closed", value: comp.saleDate });
  if (comp.daysOnMarket != null)
    facts.push({ label: "Days on market", value: comp.daysOnMarket });
  if (comp.sqft != null)
    facts.push({ label: "Approx. Sq Ft", value: comp.sqft.toLocaleString("en-US") });
  if (comp.beds != null || comp.baths != null)
    facts.push({
      label: "Layout",
      value: `${comp.beds ?? "—"} bd / ${formatBaths(comp.baths) ?? "—"} ba`,
    });
  if (comp.floor != null) facts.push({ label: "Floor", value: comp.floor });
  if (comp.ppsf != null)
    facts.push({ label: "$ / sq ft", value: `$${comp.ppsf.toLocaleString("en-US")}` });
  if (comp.condition) facts.push({ label: "Condition", value: comp.condition });

  return (
    <article
      className="flex flex-col border border-charcoal/12 bg-ivory-surface"
      data-testid={`comp-${comp.id}`}
    >
      {comp.photoUrl && (
        <div className="overflow-hidden">
          <img
            src={comp.photoUrl}
            alt={`${comp.unit}${comp.address ? ` · ${comp.address}` : ""}`}
            loading="lazy"
            decoding="async"
            className="aspect-[3/2] w-full object-cover"
          />
        </div>
      )}

      <div className="flex flex-1 flex-col p-6 md:p-7">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-serif text-2xl text-charcoal md:text-3xl">
            Unit {comp.unit}
          </h3>
          {comp.saleType && (
            <span className="shrink-0 text-[0.6rem] uppercase tracking-[0.18em] text-charcoal-muted">
              {comp.saleType}
            </span>
          )}
        </div>

        {facts.length > 0 && (
          <dl className="mt-5 grid grid-cols-2 gap-x-4 gap-y-4 border-t border-charcoal/10 pt-5 sm:grid-cols-3">
            {facts.map((f) => (
              <div key={f.label}>
                <dd className="text-base text-charcoal md:text-lg">{f.value}</dd>
                <dt className="mt-0.5 text-[0.62rem] uppercase tracking-[0.14em] text-charcoal-muted">
                  {f.label}
                </dt>
              </div>
            ))}
          </dl>
        )}

        {comp.whyItMatters && (
          <div className="mt-6 border-l-2 border-brass bg-brass-soft p-5">
            <DataTag kind="interpretation">Why this comp matters</DataTag>
            <p className="mt-3 text-sm leading-relaxed text-charcoal-soft md:text-base">
              {comp.whyItMatters}
            </p>
          </div>
        )}
      </div>
    </article>
  );
};

export const SA_Comps: React.FC<Props> = ({ comps, intro }) => {
  if (!comps?.length) return null;

  // Preserve order while grouping by optional `group`.
  const groups: { name?: string; items: SAComp[] }[] = [];
  for (const c of comps) {
    const last = groups[groups.length - 1];
    if (last && last.name === c.group) last.items.push(c);
    else groups.push({ name: c.group, items: [c] });
  }

  return (
    <section
      className="w-full bg-ivory-alt py-20 md:py-28"
      data-testid="sa-comps"
      aria-label="The most relevant sales"
    >
      <div className="mx-auto w-full max-w-6xl px-6 md:px-10">
        <Reveal>
          <Overline>The most relevant sales</Overline>
          <h2 className="mt-4 max-w-2xl font-serif text-3xl leading-tight text-charcoal md:text-4xl lg:text-5xl">
            The sales I'd pay attention to.
          </h2>
          {intro && (
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-charcoal-soft md:text-lg">
              {intro}
            </p>
          )}
          <DataLegend className="mt-6" />
        </Reveal>

        <div className="mt-12 space-y-14">
          {groups.map((g, gi) => (
            <div key={gi}>
              {g.name && (
                <Reveal>
                  <h3 className="mb-6 flex items-center gap-3 text-sm uppercase tracking-[0.18em] text-charcoal">
                    <span className="h-px w-6 bg-brass" />
                    {g.name}
                  </h3>
                </Reveal>
              )}
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                {g.items.map((comp, i) => (
                  <Reveal key={comp.id} delay={(i % 3) * 0.08}>
                    <CompCard comp={comp} />
                  </Reveal>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
